<template>
  <InviteContactModal
    v-model:name="name"
    v-model:email="email"
    :name-disabled="nameDisabled"
    :email-disabled="emailDisabled"
    :show="show"
    :title="title"
    :on-cancel="handleOnCancel"
    :on-confirm="handleOnConfirm"
    :help-text="helpText"
    :error-banner-message="errorBannerMessage"
    :server-validation-errors="serverValidationErrors"
    :save-button-text="saveButtonText"
    :save-button-text-while-working="saveButtonTextWhileWorking"
  >
    <template #commonFields>
      <CreatableSelect
        v-model="name"
        :show-create-button="true"
        :placeholder="$t('Enter recruitment agency name')"
        :label="`${$t('Recruitment agency name')}${!nameDisabled ? '*' : ''}`"
        :loading="$apollo.loading"
        :options="recruiterAgencies"
        :search-error="searchError"
        :error="serverValidationErrors['name']"
        :disabled="nameDisabled"
        class="m-b-15"
        data-test="recruiter-name"
        @create="createMode = true"
        @select-option="handleSelect"
        @update:model-value="handleUpdateName"
      >
        <div
          class="tw-flex tw-justify-between tw-items-center tw-w-full"
          data-test="add-new-recruiter"
        >
          <div>{{ name }}</div>
          <div class="tw-text-gray-700">{{ $t('Add new recruitment agency') }}</div>
        </div>
        <template #option="{ option }">
          <div class="tw-flex tw-justify-between tw-items-center">
            <WdsAvatarLabel
              :path="getAvatarPath(option.avatar, option.id)"
              :initials="option.initials"
              :text="option.name"
              sm
            />
            <div class="tw-text-gray-600 tw-uppercase">{{ option.market }}</div>
          </div>
        </template>
        <template v-if="selectedOption.initials || selectedOption.avatar" #prefix>
          <WdsAvatar
            :initials="selectedOption.initials"
            :path="getAvatarPath(selectedOption.avatar, selectedOption.id)"
            xs
          />
        </template>
      </CreatableSelect>
      <div v-if="createMode" class="tw-mb-6">
        <label>{{ `${$t('Email address')}${!emailDisabled ? '*' : ''}` }}</label>
        <WdsInput
          v-model="email"
          :disabled="emailDisabled"
          required
          :error="serverValidationErrors['email']"
          data-test="email-input"
        />
        <WdsText v-if="createMode && !emailDisabled" secondary>
          {{ $t('Choose the email address of your main point of contact') }}
        </WdsText>
      </div>
    </template>
    <template #additionalFields>
      <div
        v-if="canConfigureRecruiterManagesWorkers"
        class="tw-mb-6"
        data-test="recruiter-manages-workers-toggle"
      >
        <label class="tw-font-medium tw-mb-2">{{ $t('Engagement type') }}</label>
        <FeatureFlagged name="msp-mvp">
          <WdsText secondary class="tw-mb-2">
            {{
              $t(
                'Default for new hires. The staffing agency can pick either type per candidate when submitting.',
              )
            }}
          </WdsText>
        </FeatureFlagged>
        <WdsStack direction-column gap-sm>
          <label class="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-mb-0">
            <input
              v-model="recruiterManagesWorkersValue"
              type="radio"
              :value="true"
              name="recruiterManagesWorkers"
              class="tw-mt-1 tw-cursor-pointer"
            />
            <WdsStack direction-column>
              <span>{{ $t('Managed by staffing agency') }}</span>
              <WdsText secondary>
                {{ $t('The staffing agency manages workers, contracts, and billing on hires.') }}
              </WdsText>
            </WdsStack>
          </label>
          <label class="tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-mb-0">
            <input
              v-model="recruiterManagesWorkersValue"
              type="radio"
              :value="false"
              name="recruiterManagesWorkers"
              class="tw-mt-1 tw-cursor-pointer"
            />
            <WdsStack direction-column>
              <span>{{ $t('Referral fee') }}</span>
              <WdsText secondary>
                {{
                  $t(
                    'Workers are engaged directly. The staffing agency receives a referral fee on payments.',
                  )
                }}
              </WdsText>
            </WdsStack>
          </label>
        </WdsStack>
        <WdsAlert
          v-if="hasHires && recruiterManagesWorkersValue !== initialManagesWorkers"
          warning
          class="tw-mt-3"
        >
          {{
            $t(
              "Changing this setting will only apply to future hires. Reach out to Worksome if there's a need to change existing engagements.",
            )
          }}
        </WdsAlert>
      </div>
      <WdsStack align-start justify-space-between gap-sm class="m-b-15">
        <div v-if="!recruiterManagesWorkersValue" class="tw-w-1/2">
          <label>{{ $t('Fee') }}</label>
          <InputCurrency v-model="recruiterFee" :currency="'%'" placeholder="0" />
        </div>
        <div class="recruiter-input-ownership">
          <label>{{ $t('Ownership days') }}</label>
          <FeatureFlagged name="msp-mvp">
            <WdsInput
              :required="!ownershipNeverExpires"
              :disabled="ownershipNeverExpires"
              type="number"
              :model-value="ownershipNeverExpires ? '' : recruiterOwnershipDays"
              :error="serverValidationErrors['recruiterOwnershipDays']"
              data-test="ownership-days-input"
              @update:model-value="updateOwnership"
            />
            <label class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-mt-2 tw-mb-0">
              <input
                v-model="ownershipNeverExpires"
                type="checkbox"
                class="tw-cursor-pointer"
                data-test="ownership-never-expires"
              />
              <WdsTooltip top global z-index="9999">
                <WdsText>{{ $t('Never expires') }}</WdsText>
                <template #tooltip-content>
                  {{
                    $t(
                      'The staffing agency keeps ownership of every engagement they bring in indefinitely. Leave it off if you want ownership to expire after a set number of days. Note: this can also be configured per engagement.',
                    )
                  }}
                </template>
              </WdsTooltip>
            </label>
            <template #is-disabled>
              <WdsInput
                placeholder="0"
                required
                type="number"
                :model-value="recruiterOwnershipDays"
                :error="serverValidationErrors['recruiterOwnershipDays']"
                data-test="ownership-days-input"
                @update:model-value="updateOwnership"
              />
            </template>
          </FeatureFlagged>
        </div>
      </WdsStack>
      <div class="tw-mb-6">
        <label>{{ $t('Candidate submission limit') }}</label>
        <WdsInput
          type="number"
          :placeholder="$t('No limit')"
          :model-value="maxCandidateSubmissions"
          :error="serverValidationErrors['maxCandidateSubmissions']"
          data-test="max-candidate-submissions"
          @update:model-value="updateMaxCandidateSubmissions"
        />
        <WdsText secondary>
          {{ $t('The most candidates this staffing agency may submit per job') }}
        </WdsText>
      </div>
      <div class="tw-mb-6">
        <label>{{ $t('Tags') }}</label>
        <TypeSelect
          :name="'tags'"
          tag-type="skills"
          :selected-in="tags"
          :show-typesuggestions="false"
          :can-add-unsupported-types="true"
        />
      </div>
      <div v-if="!messageDisabled" class="recruiter-input-message">
        <label>{{ $t('Message') }}</label>
        <WdsInputArea
          height="height"
          :placeholder="$t('Type your message here')"
          @update:model-value="updateMessage"
        />
      </div>
      <FieldableFormCustomFields
        v-model:field-values="customFieldValues"
        :context-message="
          $t('Details specified below will only be visible to you and your team members.')
        "
        :account-id="currentAccountId"
        :fieldable-type="TypeSupportingCustomFieldValues.CompanyRecruiter"
        :field-value-fragments="initialCustomFieldValues"
        disable-recruiter-input-fields
      />

      <WdsStack v-if="recruiterCustomFieldValues.length > 0" direction-column gap-sm>
        <WdsDivider align-left class="tw-my-2">
          <WdsLabel subtle xs>
            <WdsStack direction-row gap-xs align-center>
              <WdsIcon name="user" size="12" />
              <span>{{ $t('Additional information') }}</span>
            </WdsStack>
          </WdsLabel>
        </WdsDivider>

        <WdsText secondary>
          {{ $t('These fields are filled in by the staffing agency and cannot be edited.') }}
        </WdsText>

        <table class="tw-table-fixed tw-w-full">
          <thead>
            <tr class="tw-bg-true-gray-50 tw-rounded tw-text-gray-600">
              <th class="tw-font-normal tw-py-1 tw-px-2 tw-rounded-l-lg">{{ $t('Field') }}</th>
              <th class="tw-font-normal tw-py-1 tw-px-2 tw-rounded-r-lg">{{ $t('Value') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(field, index) in recruiterCustomFieldValues"
              :key="index"
              class="tw-border-b tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100"
            >
              <td class="tw-w-1/2 tw-py-1 tw-px-2">{{ field.title }}</td>
              <td class="tw-w-1/2 tw-py-1 tw-px-2">{{ field.displayValue || '–' }}</td>
            </tr>
          </tbody>
        </table>
      </WdsStack>
    </template>
  </InviteContactModal>
</template>

<script>
import gql from 'graphql-tag'
import {
  WdsAlert,
  WdsDivider,
  WdsIcon,
  WdsInput,
  WdsLabel,
  WdsStack,
  WdsAvatar,
  WdsInputArea,
  WdsAvatarLabel,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import InviteContactModal from '@/components/Contacts/InviteContactModal.vue'
import TypeSelect from '@/components/TypeSelect.vue'
import { FormValidation } from '@/utils/formValidation'
import enums from '@/utils/enum'
import InputCurrency from '@/components/InputCurrency.vue'
import { getFeeAsIntegerAndFraction } from '@/components/utilities/numberFormatting'
import CreatableSelect from '@/components/CreatableSelect.vue'
import { getAvatarPath } from '@/utils/avatar'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import {
  diffCustomFieldValueInputs,
  mapCustomFieldValueToCustomFieldValueInput,
} from '@/components/Fieldable/utils'

export default {
  components: {
    CreatableSelect,
    FeatureFlagged,
    FieldableFormCustomFields,
    InputCurrency,
    InviteContactModal,
    WdsAlert,
    WdsDivider,
    WdsIcon,
    WdsInput,
    WdsLabel,
    WdsAvatar,
    WdsInputArea,
    WdsStack,
    WdsAvatarLabel,
    WdsText,
    WdsTooltip,
    TypeSelect,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    onCancel: {
      type: Function,
      default: null,
    },
    onConfirm: {
      type: Function,
      default: null,
    },
    initialFee: {
      type: Number,
      default: null,
    },
    initialOwnership: {
      type: Number,
      default: null,
    },
    initialOwnershipNeverExpires: {
      type: Boolean,
      default: false,
    },
    initialName: {
      type: String,
      default: null,
    },
    initialEmail: {
      type: String,
      default: null,
    },
    initialTags: {
      type: Array,
      default: () => [],
    },
    nameDisabled: {
      type: Boolean,
      default: false,
    },
    emailDisabled: {
      type: Boolean,
      default: false,
    },
    messageDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Modal Title
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Modal help text
     */
    helpText: {
      type: String,
      default: null,
    },
    /**
     * Error text to display if a generic error occurs
     */
    errorText: {
      type: String,
      default: 'An error occurred',
    },
    /**
     * Text to display on save button
     */
    saveButtonText: {
      type: String,
      default: undefined,
    },
    /**
     * Text to display on save button while working
     */
    saveButtonTextWhileWorking: {
      type: String,
      default: undefined,
    },
    companyRecruiterIds: {
      type: Array,
      default: [],
    },
    manageWorkers: {
      type: Boolean,
      default: false,
    },
    canConfigureRecruiterManagesWorkers: {
      type: Boolean,
      default: false,
    },
    initialMaxCandidateSubmissions: {
      type: Number,
      default: null,
    },
    initialManagesWorkers: {
      type: Boolean,
      default: false,
    },
    hasHires: {
      type: Boolean,
      default: false,
    },
    initialCustomFieldValues: {
      type: Array,
      default: () => [],
    },
    recruiterCustomFieldValues: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      name: this.initialName || '',
      email: this.initialEmail || '',
      serverValidationErrors: {},
      errorBannerMessage: '',
      recruiterFee: getFeeAsIntegerAndFraction(this.initialFee * 0.01),
      recruiterOwnershipDays: this.initialOwnership?.toString(),
      maxCandidateSubmissions: this.initialMaxCandidateSubmissions?.toString() ?? '',
      ownershipNeverExpires: this.initialOwnershipNeverExpires,
      message: null,
      tags: [...this.initialTags],
      inputTags: null,
      recruiterAgencies: [],
      searchError: undefined,
      createMode: true,
      selectedOption: {},
      recruiterManagesWorkersValue: this.manageWorkers,
      customFieldValues: mapCustomFieldValueToCustomFieldValueInput(this.initialCustomFieldValues),
      currentAccountId: currentAccount.globalId,
      TypeSupportingCustomFieldValues,
    }
  },
  computed: {
    // Pristine baseline of the stored values, kept apart from the editable state.
    initialCustomFieldValueInputs() {
      return mapCustomFieldValueToCustomFieldValueInput(this.initialCustomFieldValues)
    },
    // Only the values the user changed in this form. Submitting untouched fields
    // would delete their stored values whenever the form holds them as empty.
    changedCustomFieldValues() {
      return diffCustomFieldValueInputs(this.customFieldValues, this.initialCustomFieldValueInputs)
    },
  },
  watch: {
    name() {
      this.removeValidationError('name')
    },
    email() {
      this.removeValidationError('email')
    },
    // The modal is teleported and stays mounted between opens, so
    // these watchers push fresh props into data on each re-open.
    initialOwnership(value) {
      this.recruiterOwnershipDays = value?.toString()
    },
    initialOwnershipNeverExpires(value) {
      this.ownershipNeverExpires = value
    },
    initialFee(value) {
      this.recruiterFee = getFeeAsIntegerAndFraction(value * 0.01)
    },
    initialName(value) {
      this.name = value || ''
    },
    initialEmail(value) {
      this.email = value || ''
    },
    initialTags(value) {
      this.tags = [...value]
    },
    manageWorkers(value) {
      this.recruiterManagesWorkersValue = value
    },
    initialCustomFieldValues(value) {
      this.customFieldValues = mapCustomFieldValueToCustomFieldValueInput(value)
    },
  },
  mounted() {
    if (!this.nameDisabled) {
      this.$apollo.queries.recruitersSearch.start()
    }
  },
  apollo: {
    recruitersSearch: {
      manual: true,
      skip: true,
      client: 'publicApi',
      query: gql`
        query recruiterAgencies($searchValue: String) {
          recruiters(search: $searchValue) {
            data {
              id
              name
              initials
              avatar
            }
          }
        }
      `,
      variables() {
        return {
          searchValue: this.name,
        }
      },
      debounce: 400,
      result({ data }) {
        if (!this.name) {
          this.recruiterAgencies = []
        } else {
          this.recruiterAgencies = data?.recruiters.data.filter(
            (recruiter) => !this.companyRecruiterIds.includes(recruiter.id),
          )
        }
      },
      error(error) {
        const { graphQLErrors } = error
        let graphQLErrorMessage = ''
        if (FormValidation.containsFormValidations(graphQLErrors)) {
          graphQLErrorMessage = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
        } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
          graphQLErrorMessage = graphQLErrors[0].message
        }
        this.searchError = `${$t('An error occurred while searching.')} ${graphQLErrorMessage}`
      },
    },
  },
  methods: {
    getAvatarPath,
    resetForm() {
      this.name = this.initialName || ''
      this.email = this.initialEmail || ''
      this.serverValidationErrors = {}
      this.recruiterFee = getFeeAsIntegerAndFraction(this.initialFee * 0.01)
      this.recruiterOwnershipDays = this.initialOwnership ?? 0
      this.maxCandidateSubmissions = this.initialMaxCandidateSubmissions?.toString() ?? ''
      this.ownershipNeverExpires = this.initialOwnershipNeverExpires
      this.errorBannerMessage = ''
      this.tags = [...this.initialTags] || []
      this.recruiterManagesWorkersValue = this.manageWorkers
      this.createMode = true
      this.selectedOption = {}
      this.customFieldValues = mapCustomFieldValueToCustomFieldValueInput(
        this.initialCustomFieldValues,
      )
    },
    handleUpdateName() {
      this.selectedOption = {}
      this.createMode = true
    },
    handleOnCancel() {
      this.resetForm()
      this.onCancel()
    },
    handleSelect(option) {
      this.selectedOption = { ...option }
      this.createMode = false
      this.name = option.name
    },
    getFeeAsDecimal(feeObj) {
      return parseFloat(`${feeObj.integer}.${feeObj.fraction}`) * 0.01
    },
    updateOwnership(value) {
      this.removeValidationError('recruiterOwnershipDays')
      this.recruiterOwnershipDays = value
      // Typing a number means the user wants finite ownership, even
      // when the feature flag hides the checkbox.
      if (value) {
        this.ownershipNeverExpires = false
      }
    },
    updateMessage(value) {
      this.message = value
    },
    updateMaxCandidateSubmissions(value) {
      this.removeValidationError('maxCandidateSubmissions')
      this.maxCandidateSubmissions = value
    },
    removeValidationError(errorName) {
      this.serverValidationErrors[`input.${errorName}`] = undefined
    },
    async handleOnConfirm() {
      this.errorBannerMessage = ''

      let recruiterFee = null
      let tags = null

      // Never-expires checked is the only path that sends null (the
      // eternal-ownership signal). Otherwise send the parsed integer
      // even when the field is empty or 0, so the backend's min:1
      // validator surfaces a clear error rather than this modal
      // silently coercing an unchecked-but-empty form into eternal.
      let recruiterOwnershipDays
      if (this.ownershipNeverExpires) {
        recruiterOwnershipDays = null
      } else {
        const parsed = parseInt(this.recruiterOwnershipDays, 10)
        recruiterOwnershipDays = Number.isFinite(parsed) ? parsed : 0
      }

      if (this.recruiterFee) {
        recruiterFee = this.getFeeAsDecimal(this.recruiterFee)
      }

      if (this.tags) {
        // eslint-disable-next-line prefer-destructuring -- Baseline eslint ignore please fix if working on this
        tags = this.tags
      }

      const parsedLimit = parseInt(this.maxCandidateSubmissions, 10)
      const maxCandidateSubmissions = Number.isFinite(parsedLimit) ? parsedLimit : null

      return this.onConfirm({
        maxCandidateSubmissions,
        recruiterOwnershipDays,
        recruiterFee,
        tags,
        email: this.email,
        name: this.name,
        message: this.message,
        createMode: this.createMode,
        id: this.selectedOption.id,
        customFieldValues: this.changedCustomFieldValues,
        ...(this.canConfigureRecruiterManagesWorkers && { managesWorkers: this.recruiterManagesWorkersValue }),
      })
        .then(() => {
          this.handleOnCancel()
        })
        .catch((error) => {
          const { graphQLErrors } = error
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.serverValidationErrors = FormValidation.getSingularGraphqlErrors(
              graphQLErrors,
              'input.',
            )
          } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
            this.errorBannerMessage = graphQLErrors[0].message
          } else {
            this.errorBannerMessage = this.errorText
            this.$store.commit('sendNotification', {
              type: enums.notification.types.ERROR,
              msg: this.errorText,
              sender: this,
              severity: enums.notification.severity.CRITICAL,
            })
          }
        })
    },
  },
}
</script>

<style scoped lang="scss">
.recruiter-input-fee {
  width: 150px;
}

.recruiter-input-ownership {
  width: 242px;
}

.recruiter-input-message {
  margin-bottom: 10px;
}
</style>
